import { flatMap, merge } from 'lodash';

import { getLogCriteria, LogCriteriaKey } from '../../declarations/log-filters/log-filters';
import { partialExpressionToExpression, validateExpressions } from '../../service/FilterHelpers/FilterHelpers';
import {
    EnumSetItem,
    FilterExpression,
    FilterExpressionEnumSetValue,
    FilterExpressionValue,
    FilterPartialExpression,
    LogQueryFilter
} from '../../typings/filter';
import { CategoryInput } from '../../typings/graphql-log';
import { LogEventCategories } from '../../typings/log';

const textExpressionToLogFilterExpression = (): null => {
    return null;
};

const mapFiltersToAPICategories = (value: FilterExpressionValue): Array<CategoryInput> => {
    return flatMap(value.value as FilterExpressionEnumSetValue, (item: EnumSetItem) => item.key as CategoryInput);
};

const logMappings = {
    toStringArray: (value: FilterExpressionValue): Array<string> => {
        return value.value as Array<string>;
    },
    toNumberArray: (value: FilterExpressionValue): Array<number> => {
        return (value.value as Array<string>).map(v => parseInt(v, 10));
    },
    toCategories: mapFiltersToAPICategories
};

const equality = (
    key: string,
    mapper: (v: FilterExpressionValue) => Array<number | string | LogEventCategories>
): ((exp: FilterExpression) => { [key: string]: {} }) => {
    return ({ value }: FilterExpression): { [key: string]: {} } => {
        return { [key]: mapper(value) };
    };
};

const logCriteriaMappings: Record<LogCriteriaKey, (expression: FilterExpression) => LogQueryFilter> = {
    cid: equality('campaignIds', logMappings.toNumberArray),
    adgroupId: equality('adGroupIds', logMappings.toNumberArray),
    bannerId: equality('adIds', logMappings.toNumberArray),
    userName: equality('logins', logMappings.toStringArray),
    general: equality('categories', logMappings.toCategories),
    statuses: equality('categories', logMappings.toCategories)
};

const expressionToLogQueryFilter = (expression: FilterExpression): LogQueryFilter => {
    return logCriteriaMappings[expression.criteriaKey](expression);
};

export const mapLogFilterToApiFormat = (expressions: FilterPartialExpression[]): LogQueryFilter => {
    const criteria = getLogCriteria();
    const validationResult = validateExpressions(criteria, expressions, true);
    const validExpressions = expressions.filter((expr, index) => validationResult[index]);
    const strictExpressions: FilterExpression[] = validExpressions.reduce((result: FilterExpression[], expression) => {
        const strictExpression = partialExpressionToExpression(
            criteria,
            textExpressionToLogFilterExpression,
            expression
        );

        if (strictExpression !== null) {
            result.push(strictExpression);
        }

        return result;
    }, []);

    return strictExpressions.map(expressionToLogQueryFilter).reduce((res: LogQueryFilter, value: LogQueryFilter) => {
        const categories = (res.categories || []).concat(value.categories || []);
        const merged = merge(res, value);
        if (categories.length > 0) {
            merged.categories = categories;
        }
        return merged;
    }, {});
};
